import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type {
  BeniMarketCompositionMarket,
  BeniSmartCompsetCategory,
} from 'lumen/models';
import {
  BENI_SMART_COMPSET_CATEGORY_TRANSLATIONS,
  BENI_SMART_COMPSET_CATEGORY_WEIGHT_TRANSLATIONS,
  type SmartCompsetCategoryWeight,
} from 'lumen/utils/constants/benchmark-insight/beni-smart-compset-constant-values';

export default class BeniSmartCompset extends Model {
  [Type] = 'benchmark-insight/competitors/smart-compset' as const;

  @belongsTo('benchmark-insight/market-composition-market', {
    async: false,
    inverse: null,
  })
  declare marketComposition: BeniMarketCompositionMarket | null;

  @hasMany('benchmark-insight/competitors/smart-compset-category', {
    async: false,
    inverse: null,
  })
  declare categories: BeniSmartCompsetCategory[];

  @attr('string') declare lastChangedDate: string;
  @attr('string') declare user: string;
  @attr('boolean') declare valid: boolean;

  @service declare intl: Services['intl'];

  get convertedWeights(): {
    name: string;
    value: string;
  }[] {
    const categoriesWithWeight = this.categories.filter(
      (
        category,
      ): category is BeniSmartCompsetCategory & {
        weight: SmartCompsetCategoryWeight;
      } => {
        return category.weight !== null;
      },
    );

    return categoriesWithWeight
      .sort((a, b) => b.weight - a.weight)
      .map((category) => {
        const { name, weight } = category;

        const keyForName = BENI_SMART_COMPSET_CATEGORY_TRANSLATIONS[name];
        const keyForWeight =
          BENI_SMART_COMPSET_CATEGORY_WEIGHT_TRANSLATIONS[weight];

        return {
          name: this.intl.t(keyForName),
          value: this.intl.t(keyForWeight),
        };
      });
  }

  get name(): string {
    return this.intl.t('benchmark_insight.competitors.smart_compset.default');
  }

  rollbackAttributes(): void {
    this.categories.forEach((category) => {
      category.rollbackAttributes();
    });
  }

  toggleCategory(toggledCategory: BeniSmartCompsetCategory): void {
    const match = this.categories.find((category) => {
      return category.name === toggledCategory.name;
    });

    if (match) {
      if (match.weight) {
        match.weight = null;
      } else {
        match.weight = 3;
      }
    }
  }

  updateWeight(category: BeniSmartCompsetCategory, weight: number): void {
    if (category.isSmartCompsetCategoryWeight(weight)) {
      category.weight = weight;
    }
  }
}
